import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  promotionalOfferAddAction,
  promotionalOfferUpdateAction,
  promotionalOfferViewAction,
} from "../../../redux/actions/promotional-offer-action";
import { storeListApi } from "../../../redux/apis/general-api";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";
import AddButton from "../../../components/buttons/button";
import InputField from "../../../components/forms/input-field";
import ItemsSearchAddFeild from "../../../components/forms/items-search-add-field";
import { productListAction } from "../../../redux/actions/general-action";
import InnerLayout from "../../../components/layouts/inner-layout";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";

const validationSchema = Yup.object().shape({
  po_name: Yup.string().required("Offer name is required"),
  po_threshold_amount: Yup.number()
    .required("Threshold amount is required")
    .min(1, "Must be greater than 0"),
  po_free_item_id: Yup.string().required("Free item is required"),
  po_free_item_quantity: Yup.number()
    .required("Quantity is required")
    .min(1, "Must be at least 1"),
  po_start_date: Yup.date().required("Start date is required"),
  po_end_date: Yup.date()
    .required("End date is required")
    .min(Yup.ref("po_start_date"), "End date must be after start date"),
  po_stores: Yup.array().min(1, "Select at least one store"),
});

const PromotionalOfferForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const po_id = searchParams.get("po_id");
  const isEdit = !!po_id;

  const [storeList, setStoreList] = useState([]);
  const [initialValues, setInitialValues] = useState({
    po_name: "",
    po_threshold_amount: "",
    po_free_item_id: "",
    po_free_item_name: "",
    po_free_item_code: "",
    po_free_item_quantity: 1,
    po_start_date: "",
    po_end_date: "",
    po_status: true,
    po_stores: [],
  });

  useEffect(() => {
    storeListApi().then((res) => {
      if (res?.data?.data) {
        setStoreList(res.data.data);
      }
    });

    if (isEdit) {
      dispatch(
        promotionalOfferViewAction(
          { po_id },
          (data) => {
            if (data?.data) {
              const offer = data.data;
              setInitialValues({
                po_name: offer.po_name || "",
                po_threshold_amount: offer.po_threshold_amount || "",
                po_free_item_id: offer.po_free_item_id || "",
                po_free_item_name: offer.po_free_item_name || "",
                po_free_item_code: offer.po_free_item_code || "",
                po_free_item_quantity: offer.po_free_item_quantity || 1,
                po_start_date: offer.po_start_date
                  ? offer.po_start_date.split("T")[0]
                  : "",
                po_end_date: offer.po_end_date
                  ? offer.po_end_date.split("T")[0]
                  : "",
                po_status: offer.po_status ?? true,
                po_stores: offer.po_stores || [],
              });
            }
          },
          () => errorToast("Failed to load offer")
        )
      );
    }
  }, []);

  const handleSubmit = (values, { resetForm }) => {
    const action = isEdit
      ? promotionalOfferUpdateAction
      : promotionalOfferAddAction;
    const payload = isEdit ? { ...values, po_id } : values;

    dispatch(
      action(
        payload,
        (data) => {
          successToast(data?.message || "Success");
          navigate("/settings/promotional-offers");
        },
        (err) => {
          errorToast(err?.data?.message || "Something went wrong");
        }
      )
    );
  };

  const fetchData = async (inputValue, callback, field) => {
    const onSuccess = (data) => {
      let rows = data?.data === null ? [] : data.data;
      const options = rows.map((item) => ({
        value: item.item_id,
        label: field === "item_code" ? item.item_code : item.item_name,
        ...item,
      }));
      callback(options);
    };
    const onError = () => {
      callback([]);
    };
    if (inputValue?.length > 0) {
      dispatch(
        productListAction(
          { field: field || "item_name", value: inputValue },
          onSuccess,
          onError
        )
      );
    } else {
      callback([]);
    }
  };

  const handleItemSelect = (selectedOption, setFieldValue) => {
    if (selectedOption) {
      setFieldValue("po_free_item_id", selectedOption.item_id || selectedOption.value);
      setFieldValue("po_free_item_name", selectedOption.item_name || selectedOption.label);
      setFieldValue("po_free_item_code", selectedOption.item_code || "");
    } else {
      setFieldValue("po_free_item_id", "");
      setFieldValue("po_free_item_name", "");
      setFieldValue("po_free_item_code", "");
    }
  };

  return (
    <InnerLayout list={true} title="Settings" module="promotional_offers">
      <div className="mx-6">
        <div className="flex justify-between pt-3 text-sml">
          <div className="pb-4 flex items-center gap-2">
            <button
              className="flex items-center gap-2"
              onClick={() => navigate("/settings/promotional-offers")}
            >
              <ChevronLeftIcon className="h-5 w-5 inline-block" />
              Discount Settings /
            </button>
            <span className="cursor-pointer">
              {isEdit ? "Edit" : "Add"} Promotional Offer
            </span>
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, handleSubmit }) => (
            <Form>
              <div
                className="flex flex-col justify-between"
                style={{ height: "80vh" }}
              >
                <div className="overflow-auto pr-4">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", maxWidth: "72rem" }}>
                    {/* Row 1 */}
                    <InputField label="Offer Name" name="po_name" placeholder="Enter offer name" isRequired={true} containerClass="mb-0" labelWidth="10rem" />
                    <div className="flex items-center m-0 p-0 w-full">
                      <label className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]" style={{ minWidth: "10rem" }}>
                        Item (Code)
                      </label>
                      <div className="w-full">
                        <ItemsSearchAddFeild label="" isLabelReq={false} name="po_free_item_code_search" field="item_code" placeholder="Search by item code..." fetchData={fetchData} isRequired={false} errorClass="hidden"
                          value={values.po_free_item_code ? { label: values.po_free_item_code, value: values.po_free_item_id } : null}
                          onChange={(opt) => handleItemSelect(opt, setFieldValue)}
                        />
                      </div>
                    </div>

                    {/* Row 2 */}
                    <InputField label="Threshold (Rs.)" name="po_threshold_amount" placeholder="Enter minimum bill amount" type="number" isRequired={true} containerClass="mb-0" labelWidth="10rem" />
                    <div className="flex items-center m-0 p-0 w-full">
                      <label className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]" style={{ minWidth: "10rem" }}>
                        Item (Name)
                      </label>
                      <div className="w-full">
                        <ItemsSearchAddFeild label="" isLabelReq={false} name="po_free_item_name" field="item_name" placeholder="Search by item name..." fetchData={fetchData} isRequired={false} errorClass="hidden"
                          value={values.po_free_item_name ? { label: values.po_free_item_name, value: values.po_free_item_id } : null}
                          onChange={(opt) => handleItemSelect(opt, setFieldValue)}
                        />
                        {touched.po_free_item_id && errors.po_free_item_id && (
                          <p className="text-red-500 text-xs mt-1">{errors.po_free_item_id}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 3 */}
                    <InputField label="Start Date" name="po_start_date" placeholder="" type="date" isRequired={true} containerClass="mb-0" labelWidth="10rem" />
                    <InputField label="Free Qty" name="po_free_item_quantity" placeholder="Enter quantity" type="number" isRequired={true} containerClass="mb-0" labelWidth="10rem" min={1} />

                    {/* Row 4 */}
                    <InputField label="End Date" name="po_end_date" placeholder="" type="date" isRequired={true} containerClass="mb-0" labelWidth="10rem" />
                    <div className="flex items-center m-0 p-0 w-full">
                      <label className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]" style={{ minWidth: "10rem" }}>
                        Status
                      </label>
                      <div className="flex items-center gap-3">
                        <div
                          className="relative w-11 h-6 rounded-full cursor-pointer transition-colors duration-200"
                          style={{ backgroundColor: values.po_status ? "var(--primary-button-bg-color)" : "#d1d5db" }}
                          onClick={() => setFieldValue("po_status", !values.po_status)}
                        >
                          <div
                            className="absolute top-[2px] h-5 w-5 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                            style={{ left: values.po_status ? "22px" : "2px" }}
                          ></div>
                        </div>
                        <span className="text-[var(--input-color)] text-[length:var(--input-font-size)]">
                          {values.po_status ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    {/* Row 5: Stores - spans full width */}
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div className="flex items-start m-0 p-0 w-full">
                        <label className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] pt-2" style={{ minWidth: "10rem" }}>
                          Applicable Stores
                        </label>
                        <div className="w-full">
                          <div className="border border-[var(--input-border)] rounded-[8px] p-2 overflow-y-auto bg-[var(--input-bg)]" style={{ maxHeight: "10rem" }}>
                            {storeList.length > 0 && (
                              <label className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-100 px-2 rounded border-b border-gray-100 mb-1">
                                <input type="checkbox" checked={values.po_stores.length === storeList.length}
                                  onChange={(e) => setFieldValue("po_stores", e.target.checked ? storeList.map((s) => s.store_id) : [])}
                                  className="w-4 h-4 rounded flex-shrink-0" />
                                <span className="text-[var(--input-color)] text-[length:var(--input-font-size)] font-medium">Select All</span>
                              </label>
                            )}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0" }}>
                              {storeList.map((store) => (
                                <label key={store.store_id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 px-2 rounded">
                                  <input type="checkbox" checked={values.po_stores.includes(store.store_id)}
                                    onChange={(e) => {
                                      const newStores = e.target.checked ? [...values.po_stores, store.store_id] : values.po_stores.filter((s) => s !== store.store_id);
                                      setFieldValue("po_stores", newStores);
                                    }}
                                    className="w-4 h-4 rounded flex-shrink-0" />
                                  <span className="text-[var(--input-color)] text-[length:var(--input-font-size)] truncate">
                                    {store.concated_store_name || store.store_name}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {storeList.length === 0 && (
                              <span className="text-[var(--input-place-color)] text-sm px-2">No stores available</span>
                            )}
                          </div>
                          {values.po_stores.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">{values.po_stores.length} store(s) selected</p>
                          )}
                          {touched.po_stores && errors.po_stores && (
                            <p className="text-red-500 text-xs mt-1">{errors.po_stores}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Selected item confirmation */}
                  {values.po_free_item_name && (
                    <div className="mt-4 ml-[10rem] px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800" style={{ maxWidth: "30rem" }}>
                      Free Item: <strong>{values.po_free_item_name}</strong>
                      {values.po_free_item_code && <span className="text-green-600 ml-1">({values.po_free_item_code})</span>}
                      {" x "}{values.po_free_item_quantity}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 items-center justify-end w-full h-[4rem]">
                  <button
                    type="button"
                    onClick={() => navigate("/settings/promotional-offers")}
                    className="px-8 py-2 border border-[var(--input-border)] rounded-md text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <AddButton
                    onclkFn={handleSubmit}
                    prefixIcon={false}
                    text={isEdit ? "Update" : "Save"}
                    type="submit"
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </InnerLayout>
  );
};

export default PromotionalOfferForm;
